import React, { useEffect, useState, useCallback } from "react";
import IntlMessages from "@iso/components/utility/intlMessages";
import { Form, Button, Select, InputNumber, DatePicker, message } from 'antd';
import _ from "lodash";
import SensorModalWrapper from './SensorModal.styles'
import { sensorService } from "@iso/services";
import { sensorSuppliers } from '@iso/constants/sensorSuppliers';
import InfiniteScrollSelect from "@iso/components/Select/InfiniteScrollSelect";
import { propertyService } from "../../../../services/property.service";
import debounce from 'lodash/debounce';
import { SensorServiceFactory } from "../../../../services/SensorServiceFactory";
import { DeviceRenderFactory } from "../../../../components/SensorFactory/DeviceRenderFactory"
import { PartNumberRenderFactory } from "../../../../components/SensorFactory/PartNumberRenderFactory";
import {
  SENSOR_TYPE_DOOR_CONTACT_SENSOR,
  SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER,
} from '@iso/constants/trackpacSensorTypes';

const { Option } = Select;

const SensorModal = (props) => {
  const cancelModal = () => {
    props.cancelModal();
    form.resetFields();
  }

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [properties, setProperties] = useState([]);

  // Watch the part_number selection so we can conditionally render the
  // litres_per_pulse input for the DOOR_CONTACT_SENSOR_WATER variant. The
  // PartNumberRenderFactory currently writes the selected value at
  // ['sensor', 'part_number', 0] (single-element array via index map), so
  // accept either an array or a bare string here.
  const watchedPartNumber = Form.useWatch(['sensor', 'part_number'], form);
  // Wrapped in useCallback to match the codebase pattern elsewhere in this
  // file (handleSearchProperty etc.) — keeps the identity stable across
  // renders even though no current consumer needs that.
  const partNumberIncludes = useCallback(
    (target) =>
      Array.isArray(watchedPartNumber)
        ? watchedPartNumber.includes(target)
        : watchedPartNumber === target,
    [watchedPartNumber]
  );
  const isWaterMeterSelected = partNumberIncludes(SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER);
  const isDoorSensorSelected = partNumberIncludes(SENSOR_TYPE_DOOR_CONTACT_SENSOR);
  // Both door variants share the same physical CPL03 pulse counter, so the
  // initial-count / calibration-time inputs apply to either one.
  const isPulseCounterVariantSelected = isDoorSensorSelected || isWaterMeterSelected;

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    getProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    setLoading(true);
    form
      .validateFields()
      .then(values => {
        onStore(values);
      })
      .catch(info => {
        setLoading(false);
        console.error('Validate Failed:', info);
      });
  };

  const onStore = (values) => {
    let terminalId = values.sensor.terminal_id;
    
    // Extract terminal_id for Trackpac and Lora AirAgri
    if (selectedSupplier === 'Trackpac' || selectedSupplier === 'Lora AirAgri' || selectedSupplier === 'Traccar AirAgri') {
      try {
        const deviceData = JSON.parse(values.sensor.terminal_id);
        terminalId = deviceData.id;
      } catch (error) {
        console.warn('Failed to parse device data, using raw value');
      }
    }

    // Pulse-counter calibration: DatePicker hands us a moment object; the
    // backend expects a "YYYY-MM-DD HH:mm:ss" string (SensorService::
    // resolveReferenceMoment parses it that way). Convert here so a
    // partially-filled form (count only, no time) still serializes cleanly.
    const initialPulseAt = values.sensor.initial_pulse_at;
    const serializedInitialPulseAt = (initialPulseAt && typeof initialPulseAt.format === 'function')
      ? initialPulseAt.format('YYYY-MM-DD HH:mm:ss')
      : (initialPulseAt || null);

    const data = {
      sensor: {
        ...values.sensor,
        terminal_id: terminalId,
        initial_pulse_at: serializedInitialPulseAt,
        // name will already be included for Trackpac and Lora AirAgri from the form
      }
    };

    sensorService.storeOrUpdate(data, props.sensorData?.id).then(res => {
      if(res.code === '0000'){
          setLoading(false);
          message.success('Add Sensor success!');
          cancelModal();
          props.updateSensorsData();
      }else{
          setLoading(false);
          message.error(res.message);
      }
    });
  }

  const getDevices = async (supplier) => {
    setDeviceList([]);
    setLoading(true);

    try {
      const sensorService = SensorServiceFactory.createService(supplier);
      const res = await sensorService.getDeviceList();
      
      if (res.code === '0000') {
        setDeviceList(res.data);
      } else {
        message.error(res.message);
      }
    } catch (error) {
      message.error('Failed to fetch devices');
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  }

  const getProperties = (page = 1, pageSize = 10, search = '') => {
    setLoading(true);

    propertyService.getPaginatedProperties(page, pageSize, search).then(res => {
      if (res.code === '0000') {
        setProperties(prevProperties => 
          page === 1 
            ? res.properties 
            : [...prevProperties, ...res.properties]
        );

        if (!search && page === 1) {
          // Store initial properties for reference if needed
        }

        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: res.pagination.totalCount,
        });
        setLoading(false);
      } else {
        setLoading(false);
        message.error(res.message);
      }
    });
  }

  const onSearchPropertyChange = (value) => {
    setSearchTerm(value);
    handleSearchProperty(value);
  }

  const handleSearchProperty = useCallback(
    debounce((value) => {
      getProperties(1, pagination.pageSize, value);
    }, 500),
    [pagination.pageSize] // Add pagination.pageSize as dependency
  );

  const handlePageChange = (page, pageSize) => {
    if (page <= Math.ceil(pagination.total / pagination.pageSize)) {
      getProperties(page, pageSize, searchTerm);
    }
  }

  const onChangeSupplier = (value) => {
    getDevices(value);
    setSelectedSupplier(value);
    setFields([]);
  }

  const onChangeDevice = (value) => {
    try {
      // For Trackpac and Lora AirAgri, value is a JSON string with id and name
      if (selectedSupplier === 'Trackpac' || selectedSupplier === 'Lora AirAgri' || selectedSupplier === 'Traccar AirAgri') {
        const deviceData = JSON.parse(value);
        setSelectedDevice(deviceData.id);
        
        // Set the device name in the form
        form.setFieldsValue({
          sensor: {
            ...form.getFieldsValue().sensor,
            name: deviceData.name
          }
        });
      } else {
        // For RF Technology, value is just the device string
        setSelectedDevice(value);
      }
    } catch (error) {
      // Fallback for RF Technology or if JSON parsing fails
      setSelectedDevice(value);
    }
  
  setFields([]);
};



  const renderSupplierOptions = () => {
    const options = _.map(sensorSuppliers, (supplier, index) => (
      <Option key={index} value={supplier.value}>
        <div>{supplier.label}</div>
      </Option>
    ));

    return (
      <Select onChange={onChangeSupplier} placeholder="Select a vendor" allowClear size="large">
        {options}
      </Select>
    );
  };

  const renderDeviceOptions = () => {
    if (!selectedSupplier || deviceList.length === 0) {
      return (
        <Select placeholder="Select a device" allowClear size="large" disabled>
        </Select>
      );
    }

    const deviceRenderer = DeviceRenderFactory.createRenderer(selectedSupplier);
    return deviceRenderer.renderOptions(deviceList, onChangeDevice);
  }

  const renderPartNumberSection = () => {
    if (!selectedSupplier) return null;

    const partNumberRenderer = PartNumberRenderFactory.createRenderer(
      selectedSupplier, 
      deviceList, 
      selectedDevice
    );

    return (
      <Form.Item>
        {partNumberRenderer.renderFormItems()}
      </Form.Item>
    );
  }

  return (
    <SensorModalWrapper
      className='sensor-modal-wrapper'
      forceRender={true}
      open={props.visibleSensorModal}
      title={<IntlMessages id="Add Sensor" />}
      onCancel={cancelModal}
      maskClosable={true}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="horizontal"
        fields={fields}
      > 
        <Form.Item
          name={['sensor', 'property_id']}
          label="Property"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          <InfiniteScrollSelect
            showSearch
            placeholder={<IntlMessages id="propertyPage.modal.selectAPropertyPlaceholder" />}
            size="large"
            allowClear
            filterOption={false}
            onSearch={onSearchPropertyChange}
            options={(properties || []).map((property) => ({
              value: property.id,
              label: property.name,
            }))}
            onLoadMore={() => handlePageChange(pagination.current + 1, pagination.pageSize)}
          />
        </Form.Item>

        <Form.Item
          name={['sensor', 'supplier']}
          label="Vendor"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          { renderSupplierOptions() }
        </Form.Item>
          
        <Form.Item
          name={['sensor', 'terminal_id']}
          label="Device"
          rules={[
            {
              required: true,
              message: 'This field is required',
            },
          ]}
        >
          { renderDeviceOptions() }
        </Form.Item>
          
          {(selectedSupplier === 'Trackpac' || selectedSupplier === 'Lora AirAgri' || selectedSupplier === 'Traccar AirAgri') && (
          <Form.Item
            name={['sensor', 'name']}
            style={{ display: 'none' }}
          >
            <input type="hidden" />
          </Form.Item>
        )}
        
        { renderPartNumberSection() }

        {isWaterMeterSelected && (
          <Form.Item
            name={['sensor', 'litres_per_pulse']}
            label="Litres per pulse"
            initialValue={1}
            tooltip="The L/pulse ratio printed on your water meter (typical residential = 1, medium-bore = 10, bulk = 100). m³ on the card is computed as total_pulses × this value ÷ 1000."
            rules={[
              { required: true, message: 'Required for DOOR_CONTACT_SENSOR_WATER' },
              { type: 'integer', min: 1, message: 'Must be an integer ≥ 1' },
            ]}
          >
            <InputNumber min={1} step={1} style={{ width: '100%' }} size="large" />
          </Form.Item>
        )}

        {/*
          Pulse-counter calibration (door + water variants). Both fields are
          OPTIONAL — if neither is set, the displayed count tracks the
          device's raw total_pulse from 0. If only the count is set, "now" is
          treated as the calibration moment. If the time is also set, the
          backend looks up the device's actual pulse at that moment and uses
          (entered_count − actual_pulse_at_that_moment) as a constant offset
          applied to every cumulative reading.

          Gated by supplier === 'Lora AirAgri' because the reference-pulse
          lookup queries lora.airagri.tech's frame history. SensorService::
          createSensors() short-circuits the same way server-side: if those
          fields arrive with any other supplier, they're silently dropped
          (just a Yii::warning is logged) — so hiding the inputs entirely
          prevents Trackpac / Traccar / ERoad admins from filling them in
          and getting no effect with no UI feedback.
        */}
        {isPulseCounterVariantSelected && selectedSupplier === 'Lora AirAgri' && (
          <>
            <Form.Item
              name={['sensor', 'initial_pulse_count']}
              label={isWaterMeterSelected ? 'Initial pulse count (optional)' : 'Initial counter value (optional)'}
              tooltip={
                isWaterMeterSelected
                  ? 'The cumulative pulse count you want the meter card to display from. m³ is computed from this × L/pulse ÷ 1000. Leave blank to track from 0.'
                  : 'The cumulative count you want the door counter to display from. Leave blank to track from 0.'
              }
              rules={[
                { type: 'integer', min: 0, message: 'Must be a non-negative integer' },
              ]}
            >
              <InputNumber min={0} step={1} style={{ width: '100%' }} size="large" placeholder="e.g. 1000" />
            </Form.Item>

            <Form.Item
              name={['sensor', 'initial_pulse_at']}
              label="Calibration time (optional)"
              tooltip="The moment the initial count above applies from. The backend looks up the device's actual pulse reading at this time and uses it as the reference point. Leave blank to use the current moment."
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }}
                size="large"
                placeholder="Select date and time"
              />
            </Form.Item>
          </>
        )}

      </Form>
    </SensorModalWrapper>
  );
};

export default SensorModal;